/* Version: 3.5.155 */
'use client';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { useRef } from 'react';
import { useDialog, useOverlay, useModal } from 'react-aria';
import PropTypes from 'prop-types';
import { safeRest, noop } from '../../utils/functions.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import { Backdrop, ModalContent } from './styledComponents.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import useTheme from '../../hooks/useTheme.js';

var _excluded = ["id", "children", "customStyle", "ariaLabel", "ariaLabelledBy", "ariaDescribedBy", "isOpen", "onClose", "isDismissable", "isKeyboardDismissDisabled"];
var ModalContainer = function ModalContainer(_ref) {
  var id = _ref.id,
    children = _ref.children,
    _ref$customStyle = _ref.customStyle,
    customStyle = _ref$customStyle === undefined ? {
      modalStyle: {}
    } : _ref$customStyle,
    ariaLabel = _ref.ariaLabel,
    ariaLabelledBy = _ref.ariaLabelledBy,
    ariaDescribedBy = _ref.ariaDescribedBy,
    _ref$isOpen = _ref.isOpen,
    isOpen = _ref$isOpen === undefined ? false : _ref$isOpen,
    _ref$onClose = _ref.onClose,
    onClose = _ref$onClose === undefined ? noop : _ref$onClose,
    _ref$isDismissable = _ref.isDismissable,
    isDismissable = _ref$isDismissable === undefined ? true : _ref$isDismissable,
    _ref$isKeyboardDismis = _ref.isKeyboardDismissDisabled,
    isKeyboardDismissDisabled = _ref$isKeyboardDismis === undefined ? true : _ref$isKeyboardDismis,
    rest = _objectWithoutProperties(_ref, _excluded);
  var theme = useTheme();
  var safeProps = safeRest(rest);
  var _safeCustomStyle = safeCustomStyle(customStyle),
    modalStyle = _safeCustomStyle.modalStyle;

  // a11y props
  var ref = useRef();
  var _useDialog = useDialog(_objectSpread({
      id: id
    }, rest), ref),
    dialogProps = _useDialog.dialogProps,
    titleProps = _useDialog.titleProps;
  var _useOverlay = useOverlay(_objectSpread({
      isOpen: isOpen,
      onClose: onClose,
      isDismissable: isDismissable,
      isKeyboardDismissDisabled: isKeyboardDismissDisabled
    }, rest), ref),
    overlayProps = _useOverlay.overlayProps;
  var _useModal = useModal(),
    modalProps = _useModal.modalProps;

  // If ariaLabelledBy is supplied, that means user wants to provide their own element,
  // so we need to override the props generated by react-aria
  if (ariaLabelledBy) {
    dialogProps['aria-labelledby'] = ariaLabelledBy;
  }
  if (ariaDescribedBy) {
    dialogProps['aria-describedby'] = ariaDescribedBy;
  }
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(Backdrop, {
      "data-testid": "backdrop",
      "aria-hidden": true,
      theme: theme
    }), /*#__PURE__*/jsxs(ModalContent, _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
      id: id,
      ref: ref,
      modalStyle: modalStyle,
      theme: theme
    }, overlayProps), modalProps), dialogProps), safeProps), {}, {
      children: [/*#__PURE__*/jsx("span", _objectSpread(_objectSpread({
        "aria-hidden": true,
        hidden: true
      }, titleProps), {}, {
        children: ariaLabel
      })), children]
    }))]
  });
};
process.env.NODE_ENV !== "production" ? ModalContainer.propTypes = {
  /** ID is required for Automated Testing, Google analytics and Google Tag Manager */
  id: PropTypes.string,
  /** aria-label attributes for Modal Content. Dialog modal should have either this or aria-labelledby */
  ariaLabel: PropTypes.string,
  /** aria-labelledby that refers to a visible dialog title. Dialog modal should have either this or aria-label */
  ariaLabelledBy: PropTypes.string,
  /** Indicate whether the modal is displayed */
  isOpen: PropTypes.bool,
  /** onClick handler for optional close button, backdrop and ESC key */
  onClose: PropTypes.func,
  /** Indicate if clicking on back drop should close the modal or not */
  isDismissable: PropTypes.bool,
  /** Indicate if pressing on escape key should close the modal or not */
  isKeyboardDismissDisabled: PropTypes.bool,
  /** Content of Modal */
  children: PropTypes.node,
  /** Style override */
  customStyle: PropTypes.shape({
    modalStyle: PropTypes.object
  })
} : undefined;
process.env.NODE_ENV !== "production" ? ModalContainer.displayName = 'Modal' : undefined;

export { ModalContainer as default };
